import { GRAVITY, PITCH_MAX, RUN_SPEED, WALK_SPEED, type WalkTouchInput } from './types.js';

// Inc C — normalizza una KeyboardEvent.key in un token coerente per
// walkKeysHeld: Shift → 'shift', Control → 'ctrl', altri tasti a 1 char
// (lettere WASD, Space) in minuscolo, il resto invariato.
export function normalizeWalkKey(key: string): string {
  if (key === 'Shift') return 'shift';
  if (key === 'Control') return 'ctrl';
  return key.length === 1 ? key.toLowerCase() : key;
}

// Inc C — RUN: Shift tenuto premuto → RUN_SPEED, altrimenti WALK_SPEED.
export function selectWalkSpeed(keys: ReadonlySet<string>): number {
  return keys.has('shift') ? RUN_SPEED : WALK_SPEED;
}

// Inc C — CROUCH: avvicina l'altezza occhi corrente al target di al massimo
// `rate * dt` metri per tick, senza overshoot. Monotona verso il target.
export function approachEyeHeight(
  current: number,
  target: number,
  rate: number,
  dt: number,
): number {
  if (current === target) return target;
  const maxDelta = rate * Math.max(0, dt);
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
}

// CONVENTION (Codex iter 2 plan MAJOR fix):
// - Three.js right-handed Y-up.
// - Camera at origin with no rotation looks at -Z = "forward into the scene".
// - yaw is rotation around +Y (right-hand rule). +yaw turns the view to
//   the LEFT when looking down from above (toward -X for yaw = π/2).
// - forwardInput = (W ? 1 : 0) - (S ? 1 : 0)   // +1 = forward, -1 = backward
// - strafeInput  = (D ? 1 : 0) - (A ? 1 : 0)   // +1 = right, -1 = left
//
// Derivation:
//   forwardVec(yaw) = (-sin yaw, 0, -cos yaw)
//   rightVec(yaw)   = ( cos yaw, 0, -sin yaw)   // forward rotated -90° around Y
//   worldDx = forward * forwardVec.x + strafe * rightVec.x
//           = strafe*cos - forward*sin
//   worldDz = forward * forwardVec.z + strafe * rightVec.z
//           = -strafe*sin - forward*cos
//
// Sanity yaw=0,    W only:  (0, -1)  ✓ forward = -Z
// Sanity yaw=π/2,  W only:  (-1, 0)  ✓ -X (yaw positive turns LEFT)
// Sanity yaw=0,    D only:  (+1, 0)  ✓ +X
// Sanity yaw=π/2,  D only:  (0, -1)  ✓ -Z (right of -X-forward)
// Sanity yaw=π/4,  W+D:     hypot==1 (NO cancellation)
export function computeVelocity(
  keys: ReadonlySet<string>,
  yaw: number,
  speed: number,
  touchInput: WalkTouchInput | null = null,
): { dx: number; dz: number } {
  // WS5 PR4a — Mobile FP. Se il joystick virtuale ha un input attivo, ha
  // la priorita' sui tasti WASD (un eventuale Bluetooth keyboard mobile
  // verra' supportato in una iterazione futura via merge esplicito). Il
  // joystick fornisce gia' forward/strafe normalizzati a magnitude<=1 e
  // la speed assoluta in m/s (la soglia run l'ha decisa lui in base a
  // magnitude>=JOYSTICK_RUN_THRESHOLD).
  if (touchInput !== null) {
    const ndForward = touchInput.forward;
    const ndStrafe = touchInput.strafe;
    if (ndForward === 0 && ndStrafe === 0) return { dx: 0, dz: 0 };
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    const worldDx = ndStrafe * cos - ndForward * sin;
    const worldDz = -ndStrafe * sin - ndForward * cos;
    return { dx: worldDx * touchInput.speed, dz: worldDz * touchInput.speed };
  }
  const forward = (keys.has('w') ? 1 : 0) - (keys.has('s') ? 1 : 0);
  const strafe = (keys.has('d') ? 1 : 0) - (keys.has('a') ? 1 : 0);
  if (forward === 0 && strafe === 0) return { dx: 0, dz: 0 };
  // Normalize so diagonal isn't 1.41× faster than cardinal.
  const mag = Math.hypot(forward, strafe);
  const ndForward = forward / mag;
  const ndStrafe = strafe / mag;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  const worldDx = ndStrafe * cos - ndForward * sin;
  const worldDz = -ndStrafe * sin - ndForward * cos;
  return { dx: worldDx * speed, dz: worldDz * speed };
}

// WS5 PR4a — Map joystick (touchX, touchY) → WalkTouchInput.
// `centerX/Y` = origine joystick base; `radiusPx` = raggio base (es.
// JOYSTICK_RADIUS_PX = 80). Convenzione: touch sopra il centro
// (touchY < centerY) → forward positivo. NaN/Infinity guard → input zero.
// `runThreshold` = soglia magnitude per scatto a RUN_SPEED (es. 0.7).
// `deadzone` = magnitudine sotto la quale ritorniamo input zero (anti-jitter).
export function computeJoystickInput(
  touchX: number,
  touchY: number,
  centerX: number,
  centerY: number,
  radiusPx: number,
  deadzone: number,
  runThreshold: number,
): WalkTouchInput {
  if (
    !Number.isFinite(touchX) ||
    !Number.isFinite(touchY) ||
    !Number.isFinite(centerX) ||
    !Number.isFinite(centerY) ||
    radiusPx <= 0
  ) {
    return { forward: 0, strafe: 0, speed: 0 };
  }
  const dx = touchX - centerX;
  const dy = touchY - centerY;
  const len = Math.hypot(dx, dy);
  const magnitude = Math.min(1, len / radiusPx);
  if (magnitude < deadzone) return { forward: 0, strafe: 0, speed: 0 };
  // Direction unitaria (allungata fino al bordo, poi clampata). Forward
  // positiva quando il dito e' sopra al centro (dy < 0).
  const dirX = len === 0 ? 0 : dx / len;
  const dirY = len === 0 ? 0 : dy / len;
  const speed = magnitude >= runThreshold ? RUN_SPEED : WALK_SPEED;
  return {
    forward: -dirY * magnitude,
    strafe: dirX * magnitude,
    speed,
  };
}

// Semi-implicit Euler: vY -= G*dt; y += vY*dt. Clamp at floor + reset vY
// on touch-down so subsequent gravity ticks don't accumulate underflow.
export function applyGravity(
  y: number,
  vY: number,
  dt: number,
  floorY: number,
): { y: number; vY: number; onFloor: boolean } {
  const newVY = vY - GRAVITY * dt;
  const newY = y + newVY * dt;
  if (newY <= floorY) {
    return { y: floorY, vY: 0, onFloor: true };
  }
  return { y: newY, vY: newVY, onFloor: false };
}

export function clampPitch(pitch: number): number {
  return Math.max(-PITCH_MAX, Math.min(PITCH_MAX, pitch));
}

// Three.js right-handed: forward = -Z at yaw=0, pitch=0.
// Sanity yaw=0,   pitch=0:    (0, 0, -1) — forward = -Z
// Sanity yaw=π/2, pitch=0:    (-1, 0, 0) — yaw positive rotates view LEFT toward -X
// Sanity yaw=0,   pitch=π/2:  (0, 1, 0)  — pitch positive looks UP
export function forwardVector(yaw: number, pitch: number): [number, number, number] {
  const cosP = Math.cos(pitch);
  const x = -Math.sin(yaw) * cosP;
  const y = Math.sin(pitch);
  const z = -Math.cos(yaw) * cosP;
  return [x, y, z];
}
